import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  CreditCard, DollarSign, Gift, CheckCircle, Clock, XCircle, Loader2, ArrowRight
} from 'lucide-react';

const Payments = () => {
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPackage, setProcessingPackage] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const fetchData = async () => {
    try {
      const [packagesRes, historyRes] = await Promise.all([
        api.getDepositPackages(),
        api.getPaymentHistory()
      ]);
      setPackages(packagesRes.data.packages);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to load payments data');
    } finally {
      setLoading(false);
    }
  };

  // Check for session_id in URL (returning from Stripe)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await api.getPaymentStatus(sessionId);
      
      if (response.data.status === 'completed') {
        toast.success(`Payment successful! ${response.data.amount} + ${response.data.bonus} bonus credited to your account.`);
        setCheckingPayment(false);
        fetchData();
        // Clear URL params
        window.history.replaceState({}, '', '/payments');
        return;
      } else if (response.data.status === 'failed') {
        toast.error('Payment failed or expired. Please try again.');
        setCheckingPayment(false);
        window.history.replaceState({}, '', '/payments');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      toast.error('Error checking payment status');
      setCheckingPayment(false);
    }
  };

  const handleDeposit = async (packageId) => {
    setProcessingPackage(packageId);
    try {
      const response = await api.createDeposit({
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deposit');
      setProcessingPackage(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const statusIcons = {
    completed: <CheckCircle className="w-4 h-4 text-emerald-400" />,
    pending: <Clock className="w-4 h-4 text-amber-400" />,
    failed: <XCircle className="w-4 h-4 text-red-400" />
  };

  const statusColors = {
    completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Payment Status Check Overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="w-80">
            <CardContent className="p-6 text-center">
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
              <p className="font-medium">Verifying Payment...</p>
              <p className="text-sm text-muted-foreground">Please wait while we confirm your payment</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Payments</h1>
        <p className="text-muted-foreground">Deposit funds to your trading account</p>
      </div>

      {/* Deposit Packages */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {packages.map((pkg) => (
          <Card 
            key={pkg.id} 
            className={`card-hover relative overflow-hidden ${pkg.id === 'premium' ? 'border-primary' : ''}`}
            data-testid={`deposit-package-${pkg.id}`}
          >
            {pkg.id === 'premium' && (
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-bl">
                Best Value
              </div>
            )}
            <CardHeader className="pb-2">
              <CardTitle className="text-lg capitalize">{pkg.id}</CardTitle>
              <CardDescription>Deposit Package</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-4">
                <p className="text-3xl font-bold">{formatCurrency(pkg.amount)}</p>
                {pkg.bonus > 0 && (
                  <div className="flex items-center justify-center gap-1 mt-2 text-emerald-400">
                    <Gift className="w-4 h-4" />
                    <span className="text-sm">+{formatCurrency(pkg.bonus)} bonus</span>
                  </div>
                )}
              </div>
              
              <Button 
                className="w-full" 
                onClick={() => handleDeposit(pkg.id)}
                disabled={processingPackage !== null}
                data-testid={`deposit-btn-${pkg.id}`}
              >
                {processingPackage === pkg.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Deposit Now
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment Methods Info */}
      <Card className="bg-muted/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <CreditCard className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-medium mb-1">Secure Payment Processing</h3>
              <p className="text-sm text-muted-foreground">
                All payments are processed securely through Stripe. We accept all major credit cards, 
                debit cards, and digital wallets. Your financial information is encrypted and never stored on our servers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions yet</p>
              <p className="text-sm">Make your first deposit to start trading</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Method</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((tx) => (
                  <TableRow key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                    <TableCell className="text-muted-foreground">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="capitalize">{tx.type}</TableCell>
                    <TableCell className="font-mono">
                      {formatCurrency(tx.amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`gap-1 ${statusColors[tx.status]}`}>
                        {statusIcons[tx.status]}
                        {tx.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{tx.payment_method}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Payments;
